/**
 * Home Route
 * Main dashboard page - layout provided by _authenticated parent route
 * Authentication and RecordingProvider inherited from parent
 */

import { useEffect, useRef } from 'react'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { HomePage } from '../../pages/Home/HomePage'
import { useRecordingContext } from '../../contexts/RecordingContext'
import { useAuth } from '../../hooks/useAuth'

interface SearchParams {
  sessionId?: string
  patientId?: string
  patientName?: string
  patientInitials?: string
  autoStart?: boolean
  /** True when arriving from a Resume action, drives the "continuing note" banner */
  resume?: boolean
}

/**
 * Home Route Component
 * Consumes recording state from RecordingContext
 * Layout (MainLayout, Sidebar, sessions panel) provided by parent _authenticated route
 */
const HomeRouteComponent = () => {
  const search = useSearch({ from: '/_authenticated/home' }) as SearchParams
  const navigate = useNavigate()
  const { user } = useAuth()
  const {
    isRecording,
    isPaused,
    elapsedTime,
    patientInfo,
    transcription,
    liveTranscription,
    soapNote,
    isSoapGenerating,
    soapError,
    generatedNote,
    isNoteGenerating,
    noteError,
    processingStage,
    pipelineStage,
    isFinishing,
    audioQuality,
    audioQualityMetrics,
    uploadError,
    canRetryUpload,
    pendingAudioBlob,
    wsConnected,
    wsProcessing,
    wsError,
    wsReconnecting,
    wsReconnectAttempt,
    wsMaxReconnectAttempts,
    startRecording,
    togglePause,
    finishRecording,
    cancelRecording,
    resetGeneratedNote,
    dismissProcessingState,
    sendToBackground,
    failedEncounterId,
    retryUpload,
    getAnalyserNode,
    onUploadTaskFound,
    setUploadFinishing,
    currentEncounterId,
    backgroundJobs,
  } = useRecordingContext()
  const leaveHomeStateRef = useRef({
    isFinishing,
    isNoteGenerating,
    isSoapGenerating,
    currentEncounterId,
    failedEncounterId,
    backgroundJobs,
  })

  useEffect(() => {
    leaveHomeStateRef.current = {
      isFinishing,
      isNoteGenerating,
      isSoapGenerating,
      currentEncounterId,
      failedEncounterId,
      backgroundJobs,
    }
  }, [
    backgroundJobs,
    currentEncounterId,
    failedEncounterId,
    isFinishing,
    isNoteGenerating,
    isSoapGenerating,
  ])

  const handleStartNewSession = (mode: 'same' | 'new', details?: { patientName?: string; patientInitials?: string }) => {
    if (!generatedNote) {
      sendToBackground()
    }
    navigate({
      to: '/home',
      search:
        mode === 'same'
          ? {
              patientName: details?.patientName,
              patientInitials: details?.patientInitials,
            }
          : {},
      replace: true,
    })
  }

  useEffect(() => {
    return () => {
      const {
        isFinishing: wasFinishing,
        isNoteGenerating: wasNoteGenerating,
        isSoapGenerating: wasSoapGenerating,
        currentEncounterId: latestEncounterId,
        failedEncounterId: latestFailedEncounterId,
        backgroundJobs: latestBackgroundJobs,
      } = leaveHomeStateRef.current

      const hasForegroundGeneration = wasFinishing || wasNoteGenerating || wasSoapGenerating
      const alreadyBackgrounded = latestEncounterId
        ? latestBackgroundJobs.some((job) => job.encounterId === latestEncounterId)
        : false

      if (hasForegroundGeneration && latestEncounterId && !alreadyBackgrounded && !latestFailedEncounterId) {
        sendToBackground()
      }
    }
  }, [sendToBackground])

  return (
    <HomePage
      userName={user?.firstName || 'User'}
      initialPatientName={search.patientName}
      initialPatientInitials={search.patientInitials}
      initialPatientId={search.patientId}
      initialSessionId={search.sessionId}
      initialAutoStart={search.autoStart}
      initialResume={search.resume}
      isRecording={isRecording}
      isPaused={isPaused}
      elapsedTime={elapsedTime}
      transcription={transcription}
      liveTranscription={liveTranscription}
      onStartTranscription={startRecording}
      onTogglePause={togglePause}
      onFinishRecording={finishRecording}
      onCancelRecording={cancelRecording}
      onDismissProcessing={dismissProcessingState}
      onStartNewSession={handleStartNewSession}
      patientInfo={patientInfo}
      generatedNote={generatedNote}
      isSoapGenerating={isSoapGenerating}
      soapNote={soapNote}
      soapError={soapError}
      isNoteGenerating={isNoteGenerating}
      noteError={noteError}
      processingStage={processingStage}
      pipelineStage={pipelineStage}
      isFinishing={isFinishing}
      audioQuality={audioQuality}
      audioQualityMetrics={audioQualityMetrics}
      uploadError={uploadError}
      canRetryUpload={canRetryUpload}
      pendingAudioBlob={pendingAudioBlob}
      onRetryUpload={retryUpload}
      wsConnected={wsConnected}
      wsProcessing={wsProcessing}
      wsError={wsError}
      wsReconnecting={wsReconnecting}
      wsReconnectAttempt={wsReconnectAttempt}
      wsMaxReconnectAttempts={wsMaxReconnectAttempts}
      getAnalyserNode={getAnalyserNode}
      onUploadTaskFound={onUploadTaskFound}
      setUploadFinishing={setUploadFinishing}
    />
  )
}

/**
 * Route Configuration
 * Path: /_authenticated/home
 * - Authentication inherited from _authenticated parent (no requireAuth needed)
 * - RecordingProvider inherited from _authenticated parent
 * - MainLayout structure inherited from _authenticated parent
 */
export const Route = createFileRoute('/_authenticated/home')({
  component: HomeRouteComponent,
})
